/**
 * LoginPage - Simple centered login form.
 *
 * Features:
 * - Orchard logo
 * - Email and password inputs
 * - Sign in button
 * - Redirects handled by AuthSyncAndRedirect in __root.tsx
 */

import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { AuthServiceError } from '@/common/services/auth'
import { Input } from '@/common/components/input'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import { GoogleSignInButton } from '@/common/components/GoogleSignInButton'
import * as styles from './login.css'

export const Route = createFileRoute({
  component: LoginPage,
})

function LoginPage() {
  const { login, error: authError } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await login(email, password)
      // Redirect handled by AuthSyncAndRedirect in __root.tsx
    } catch (err) {
      // Check if password change is required - redirect to reset page
      if (err instanceof AuthServiceError && err.token) {
        navigate({
          to: '/reset-password',
          search: {
            token: err.token,
            reason: err.reason || 'reset',
            ...(err.expires && { expires: String(err.expires) }),
          },
        })
        return
      }

      // Handle password change required but no token (edge case)
      // This can happen if FusionAuth doesn't return a changePasswordId
      if (err instanceof AuthServiceError && err.status === 403) {
        setError('Your password must be reset. Please use "Forgot password?" to get a new reset link.')
        return
      }

      // Use the error message from the exception if available
      if (err instanceof AuthServiceError) {
        setError(err.message)
      } else {
        setError(authError || 'Invalid email or password')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Cash Offer"
          className={styles.logo}
        />

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <Text variant="bodySm" color="error" align="center" className={styles.error}>
              {error}
            </Text>
          )}

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            error={!!error}
            autoComplete="email"
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            error={!!error}
            autoComplete="current-password"
          />

          <Link to="/forgot-password" className={styles.forgotPassword}>
            Forgot password?
          </Link>

          <Button
            type="submit"
            width="full"
            disabled={isSubmitting || !email || !password}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className={styles.divider}>
          <Text as="span" variant="bodySm" color="muted" className={styles.dividerText}>
            or
          </Text>
        </div>

        <GoogleSignInButton
          onError={(err) => setError(err)}
          disabled={isSubmitting}
        />

        <Link to="/" className={styles.backLink}>
          Back to Home
        </Link>
      </div>
    </div>
  )
}
